import React, { useState, useEffect, useRef } from 'react';
import { login, setDisplayName } from '../../../firebase/authService';
import { addStateToFirestore, getStatesFromFirestore } from '../../../firebase/stateService';
import { useAuth } from '../../../firebase/AuthContext';
import { mapAuthError } from '../../../utils/authErrors';
import { validateEmailFormat, normalizeEmail } from '../../../utils/validation';
import { hasFirebaseConfig } from '../../../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import styles from './SignIn.module.css';

export default function SignIn() {
  const [displayName, setDisplayNameState] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [errorVisible, setErrorVisible] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0); // seconds
  const [capsLock, setCapsLock] = useState(false);
  const navigate = useNavigate();
  const cooldownRef = useRef(null);
  const ready = hasFirebaseConfig();
  const { refreshUser } = useAuth() || {};

  // Auto dismiss error
  useEffect(() => {
    if (error) {
      setErrorVisible(true);
      const t = setTimeout(() => setErrorVisible(false), 5000);
      return () => clearTimeout(t);
    } else {
      setErrorVisible(false);
    }
  }, [error]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown > 0) {
      cooldownRef.current = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(cooldownRef.current);
    }
  }, [cooldown]);

  function startCooldown() {
    setCooldown(30); // 30秒ロック
  }

  const onSubmit = (e) => {
    e.preventDefault();
  setError(null);
    const normalized = normalizeEmail(email);
    if (!normalized || !password) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }
    if (!validateEmailFormat(normalized)) {
      setError('メールアドレスの形式が不正です');
      return;
    }
    if (cooldown > 0) return;
    (async () => {
      try {
        const user = await login(normalized, password);
        // 表示名が入力されていて既存と異なる場合のみ更新
        if (displayName && displayName.trim() && user && user.displayName !== displayName.trim()) {
          try {
            const updated = await setDisplayName(displayName.trim());
            if (refreshUser && updated) refreshUser();
          } catch (e) {
            console.warn('displayName 更新失敗', e);
          }
        }
        // デフォルト状態を登録
        const defaultStates = [
          '完成',
          '未組立',
          '塗装済み未組立',
          '塗装中',
          '仮組',
          '仮組途中',
          '未開封',
        ];
        if (user && user.uid) {
          const existingStates = await getStatesFromFirestore(user.uid);
          const existingNames = existingStates.map(s => s.name);
          for (const name of defaultStates) {
            if (!existingNames.includes(name)) {
              await addStateToFirestore(name, user.uid, 'post');
            }
          }
        }
        console.log('login success', user);
        navigate('/');
      } catch (err) {
        console.error(err);
        setAttempts(a => {
          const next = a + 1;
          if (next >= 5) {
            startCooldown();
            return 0; // リセット
          }
          return next;
        });
        setError(mapAuthError(err));
      }
    })();
  };

  const onKeyState = (e) => {
    if (e.getModifierState) {
      setCapsLock(e.getModifierState('CapsLock'));
    }
  };

  if (!ready) {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>サインイン</h1>
        <div style={{ textAlign:'center', lineHeight:1.6 }}>
          Firebase 設定が読み込まれていません。<br />
          runtime-firebase-config.js を配置するか configureFirebase() を実行してください。
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>サインイン</h1>
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <label className={styles.label}>
          表示名 (任意 / ログイン時に設定・変更)
          <input
            type="text"
            className={styles.input}
            value={displayName}
            onChange={(e)=>setDisplayNameState(e.target.value)}
            placeholder="Your Name"
            maxLength={40}
          />
        </label>
        <label className={styles.label}>
          メールアドレス
          <input
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </label>
        <label className={styles.label}>
          パスワード
          <div className={styles.passwordRow}>
            <input
              type={showPassword ? 'text' : 'password'}
              className={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={onKeyState}
              onKeyDown={onKeyState}
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              className={styles.toggleButton}
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
            >
              {showPassword ? '🙈' : '👁'}
            </button>
          </div>
        </label>
  {capsLock && <div className={styles.error} style={{ color:'#d97706' }}>CapsLock が有効です</div>}
  {error && errorVisible && <div className={styles.error}>{error}</div>}
  {cooldown > 0 && <div className={styles.error}>多数の失敗。{cooldown} 秒後に再試行できます</div>}
  <button type="submit" className={styles.submit} disabled={cooldown>0}>サインイン</button>
        <div className={styles.links}>
          <a href="/signup">アカウント作成</a>
          <a href="/reset-password">パスワードを忘れた?</a>
        </div>
  {attempts > 0 && cooldown === 0 && <div style={{ fontSize:12, opacity:.7 }}>失敗回数: {attempts} / 5</div>}
      </form>
    </div>
  );
}
